import { useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { TChatMessage } from "../../../../@types";
import { apis, callApi } from "../../../../api";
import { splitStringIntoChunks } from "../../../../utils";
import { useLearning } from "./useLearning";

export type TLearningMessageService = {
	messages: TChatMessage[];
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	emitChanges: () => void;
	clearData: () => void;

	setMessages: (messages: TChatMessage[]) => void;
	sendMessage: (message: TChatMessage) => void;
};

export const LearningMessageService: TLearningMessageService = {
	messages: [],
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	clearData() {
		this.messages = [];
		this.emitChanges();
	},

	setMessages(messages) {
		this.messages = messages;
	},

	sendMessage(message) {
		this.messages = [message, ...this.messages];
	},
};

export function useLearningMessageInit() {
	const { teacher } = useLearning();
	const [searchParams] = useSearchParams();
	const tab = searchParams.get("tab") ?? "";
	const isMessage = tab === "message";

	useEffect(() => {
		const getMessages = async () => {
			try {
				if (!teacher || !isMessage) {
					return;
				}
				const response = await callApi({
					method: "GET",
					url: `${apis.MESSAGES_BY_USERS}/${teacher.userId}`,
				});

				if (!response) {
					return;
				}

				const { messages } = response;

				if (messages.length > 0) {
					LearningMessageService.setMessages(messages.reverse());
				}
			} catch (error) {
			} finally {
				LearningMessageService.emitChanges();
			}
		};

		getMessages();
	}, [isMessage, teacher]);

	useEffect(() => {
		return () => {
			LearningMessageService.clearData();
		};
	}, []);
}

export function useLearningMessage() {
	const [messages, setMessages] = useState(LearningMessageService.messages);

	useEffect(() => {
		const updateData = () => {
			setMessages(LearningMessageService.messages);
		};

		LearningMessageService.addEventListener(updateData);

		return () => {
			LearningMessageService.removeEventListener(updateData);
		};
	}, []);

	const splitMessageAndSend = async (data: {
		receiverId: string;
		body: string;
	}) => {
		const splittedMessages = splitStringIntoChunks(data.body, 1000);
		for (let i = 0; i < splittedMessages.length; i++) {
			await sendMessage({
				receiverId: data.receiverId,
				body: splittedMessages[i].trim(),
			});
		}
	};

	const sendMessage = async (data: { receiverId: string; body: string }) => {
		try {
			const response = await callApi({
				method: "POST",
				url: apis.MESSAGES_CHAT,
				body: data,
			});

			if (!response) {
				return;
			}

			const message = response.chat;
			LearningMessageService.sendMessage(message);
			LearningMessageService.emitChanges();
		} catch (error) {}
	};

	return { messages, splitMessageAndSend };
}
